import { Component } from '@theme/component';

/**
 * Köttbutiken: karusell för sektionerna Kollektioner (kb-kollektioner) och Rekommenderade produkter
 * (kb-rekommenderade). Spåret scrollar med CSS scroll-snap, pilarna bläddrar så många kort som syns och
 * döljs när allt får plats. Med mus kan spåret dras. Förloppslinjen visar hur långt man har kommit.
 *
 * Lyssnarna sitter på komponenten (scroll fångas i capture-fasen), så spårets innehåll kan bytas ut
 * (personliga rekommendationer) utan att karusellen behöver kopplas om. Anropa uppdatera() efter bytet.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} spar
 * @property {HTMLElement} [pilar]
 * @property {HTMLButtonElement} [forra]
 * @property {HTMLButtonElement} [nasta]
 * @property {HTMLElement} [forlopp]
 *
 * @extends {Component<Refs>}
 */
class KbKarusell extends Component {
  /** @type {ResizeObserver | undefined} */
  #observer;

  /** @type {{ x: number, start: number, flyttad: boolean, id: number } | null} */
  #drag = null;

  #ram = 0;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('scroll', this.#schemalagg, { capture: true, passive: true });
    this.addEventListener('pointerdown', this.#borjaDra);
    this.addEventListener('click', this.#stoppaKlickEfterDrag, { capture: true });
    this.addEventListener('dragstart', this.#stoppaBilddrag);
    this.#observer = new ResizeObserver(this.#schemalagg);
    this.#observer.observe(this);
    this.uppdatera();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('scroll', this.#schemalagg, { capture: true });
    this.removeEventListener('pointerdown', this.#borjaDra);
    this.removeEventListener('click', this.#stoppaKlickEfterDrag, { capture: true });
    this.removeEventListener('dragstart', this.#stoppaBilddrag);
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#ram);
  }

  /** @param {string | number} riktning - 1 = nästa, -1 = förra */
  bladdra(riktning) {
    const { spar } = this.refs;
    const kort = spar?.firstElementChild;
    if (!(kort instanceof HTMLElement)) return;

    const gap = parseFloat(getComputedStyle(spar).columnGap) || 0;
    const kortbredd = kort.getBoundingClientRect().width + gap;
    // Bläddra så många hela kort som syns, minst ett
    const antal = Math.max(1, Math.floor((spar.clientWidth + gap) / kortbredd));
    const rtl = getComputedStyle(this).direction === 'rtl' ? -1 : 1;
    spar.scrollBy({ left: Number(riktning) * antal * kortbredd * rtl });
  }

  /** Läser av läget och uppdaterar pilar och förloppslinje. */
  uppdatera = () => {
    const { spar, pilar, forra, nasta, forlopp } = this.refs;
    if (!spar) return;

    const max = spar.scrollWidth - spar.clientWidth;
    const lage = Math.abs(spar.scrollLeft);
    const ryms = max <= 1;

    this.toggleAttribute('data-ryms', ryms);
    if (pilar) pilar.hidden = ryms;
    if (forra) forra.disabled = lage <= 1;
    if (nasta) nasta.disabled = lage >= max - 1;

    if (forlopp) {
      const andel = spar.scrollWidth > 0 ? spar.clientWidth / spar.scrollWidth : 1;
      const framsteg = max > 0 ? lage / max : 0;
      forlopp.style.setProperty('--kb-karusell-andel', String(Math.min(1, andel)));
      forlopp.style.setProperty('--kb-karusell-framsteg', String(framsteg));
    }
  };

  #schemalagg = () => {
    cancelAnimationFrame(this.#ram);
    this.#ram = requestAnimationFrame(this.uppdatera);
  };

  /** @param {PointerEvent} event */
  #borjaDra = (event) => {
    const { spar } = this.refs;
    if (event.pointerType !== 'mouse' || event.button !== 0 || !spar) return;
    if (!(event.target instanceof Node) || !spar.contains(event.target)) return;
    // Formulärelement (snabbval, variantväljare) ska gå att använda som vanligt
    if (event.target instanceof Element && event.target.closest('button, input, select, textarea, label')) return;
    if (spar.scrollWidth <= spar.clientWidth) return;

    this.#drag = { x: event.clientX, start: spar.scrollLeft, flyttad: false, id: event.pointerId };
    window.addEventListener('pointermove', this.#dra);
    window.addEventListener('pointerup', this.#slutaDra);
    window.addEventListener('pointercancel', this.#slutaDra);
  };

  /** @param {PointerEvent} event */
  #dra = (event) => {
    const { spar } = this.refs;
    if (!this.#drag || !spar || event.pointerId !== this.#drag.id) return;

    const dx = event.clientX - this.#drag.x;
    if (!this.#drag.flyttad && Math.abs(dx) < 6) return;

    if (!this.#drag.flyttad) {
      this.#drag.flyttad = true;
      spar.setAttribute('data-drar', '');
    }
    spar.scrollLeft = this.#drag.start - dx;
  };

  #slutaDra = () => {
    window.removeEventListener('pointermove', this.#dra);
    window.removeEventListener('pointerup', this.#slutaDra);
    window.removeEventListener('pointercancel', this.#slutaDra);

    const { spar } = this.refs;
    if (!spar || !this.#drag) return;

    const flyttad = this.#drag.flyttad;
    spar.removeAttribute('data-drar');
    // Klicket som avslutar en dragning ska inte öppna kortets länk
    if (flyttad) {
      this.toggleAttribute('data-nyss-dragen', true);
      setTimeout(() => this.removeAttribute('data-nyss-dragen'), 0);
    }
    this.#drag = null;
  };

  /** @param {MouseEvent} event */
  #stoppaKlickEfterDrag = (event) => {
    if (!this.hasAttribute('data-nyss-dragen')) return;
    event.preventDefault();
    event.stopPropagation();
  };

  /** @param {DragEvent} event */
  #stoppaBilddrag = (event) => {
    if (event.target instanceof Element && this.refs.spar?.contains(event.target)) event.preventDefault();
  };
}

if (!customElements.get('kb-karusell')) {
  customElements.define('kb-karusell', KbKarusell);
}
